// frontend/src/pages/WorkshopPrivateProfile.js
// The workshop's own profile — chrome/ink, French. Logo, contact details and
// company/TVA settings, editable in place.

import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Button, Field, Badge, ErrorNote, Note, Loading,
  inputClass, selectClass,
} from '../components/ui/Iparc';

const Row = ({ label, children }) => (
  <div className="py-3 border-b border-[#1E1B18] last:border-0">
    <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1">{label}</p>
    <p className="text-[13px] text-[#F4F1EA]">{children || '—'}</p>
  </div>
);

const WorkshopPrivateProfile = () => {
  const { token, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const startEdit = () => {
    setErr(''); setMsg('');
    setForm({
      businessName: profile.details?.businessName || '',
      phone: profile.details?.phone || '',
      address: profile.details?.address || '',
      taxId: profile.details?.taxId || '',
      companyType: profile.companyType || profile.details?.companyType || '',
      tvaApplicable: profile.tvaApplicable || profile.details?.tvaApplicable || '',
    });
    setEditing(true);
  };
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const saveProfile = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      const res = await axios.patch(`${API_URL}/users/${user._id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile((prev) => ({ ...(res.data.user || res.data), logo: prev?.logo }));
      setEditing(false);
      setMsg('Profil mis à jour.');
    } catch (e) {
      setErr(e.response?.data?.error || e.response?.data?.message || 'Échec de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user || !token) return;
      try {
        const res = await axios.get(`${API_URL}/users/${user._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(res.data);
      } catch (e) {
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user, token]);

  const compressAndUploadLogo = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    setErr('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxSize = 200;
            let w = img.width, h = img.height;
            if (w > h) { h = (h / w) * maxSize; w = maxSize; }
            else { w = (w / h) * maxSize; h = maxSize; }
            canvas.width = w;
            canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      await axios.post(`${API_URL}/workshops/logo`, { logo: base64 }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(prev => ({ ...prev, logo: base64 }));
    } catch (e) {
      setErr("Échec de l'envoi du logo.");
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    setErr('');
    try {
      await axios.delete(`${API_URL}/workshops/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(prev => ({ ...prev, logo: null }));
    } catch (e) {
      setErr('Échec de la suppression du logo.');
    }
  };

  if (loading) return <Page width="narrow"><Loading label="Chargement du profil…" /></Page>;
  if (!profile) return <Page title="Profil atelier" width="narrow"><ErrorNote>Profil introuvable.</ErrorNote></Page>;

  const tva = profile.tvaApplicable || profile.details?.tvaApplicable;
  const specializations = profile.details?.specializations;
  const brands = profile.details?.brands;

  return (
    <Page
      title={profile.details?.businessName || profile.name || 'Atelier'}
      subtitle="Profil atelier"
      width="narrow"
      actions={!editing && <Button variant="primary" onClick={startEdit}>Modifier</Button>}
    >
      {err && <ErrorNote>{err}</ErrorNote>}
      {msg && <Note tone="ok">{msg}</Note>}

      <Panel title="Logo">
        <div className="flex items-center gap-4">
          <div className="relative group">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => compressAndUploadLogo(e.target.files[0])}
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-20 h-20 rounded-xl bg-[#100F0D] border border-dashed border-[#2B2724]
                         hover:border-[#4E4740] flex items-center justify-center cursor-pointer
                         transition-colors overflow-hidden"
            >
              {logoUploading ? (
                <span className="text-[11px] text-[#6E665C] animate-pulse">…</span>
              ) : profile?.logo ? (
                <img src={profile.logo} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[11px] text-[#5C554C] text-center px-1">+ Logo</span>
              )}
            </div>
            {profile?.logo && (
              <button
                onClick={(e) => { e.stopPropagation(); removeLogo(); }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1A1210] border border-[#3A2622]
                           text-[#E2574B] text-[11px] flex items-center justify-center
                           opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Supprimer le logo"
              >
                ✕
              </button>
            )}
          </div>
          <p className="text-[11.5px] text-[#8F877B] max-w-[320px]">
            Le logo apparaît sur vos devis et factures en PDF. PNG, JPEG ou WebP —
            l'image est redimensionnée automatiquement.
          </p>
        </div>
      </Panel>

      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[18px]">
          <Panel title="Contact">
            <Row label="E-mail">{profile.email}</Row>
            <Row label="Téléphone">{profile.details?.phone}</Row>
            <Row label="Adresse">{profile.details?.address}</Row>
          </Panel>

          <Panel title="Entreprise">
            <Row label="Type d'entreprise">{profile.companyType || profile.details?.companyType}</Row>
            <Row label="TVA applicable">
              {tva ? <Badge tone={tva === 'Yes' ? 'ok' : 'neutral'}>{tva === 'Yes' ? 'Oui' : 'Non'}</Badge> : '—'}
            </Row>
            <Row label="N° TVA">{profile.details?.taxId}</Row>
          </Panel>

          {(specializations || brands) && (
            <Panel title="Expertise" className="md:col-span-2">
              {specializations && (
                <div className="mb-3">
                  <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-2">Spécialités</p>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(specializations) ? specializations : [specializations]).map((s, i) => (
                      <span key={i}
                            className="px-2.5 py-1 rounded-[7px] border border-[#2B2724] bg-[#141210] text-[11.5px] text-[#C9C3B6]">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {brands && (
                <div>
                  <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1">Marques prises en charge</p>
                  <p className="text-[13px] text-[#F4F1EA]">
                    {Array.isArray(brands) ? brands.join(', ') : brands}
                  </p>
                </div>
              )}
            </Panel>
          )}
        </div>
      ) : (
        <Panel title="Modifier le profil">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {[
              { name: 'businessName', label: "Nom de l'entreprise" },
              { name: 'phone', label: 'Téléphone' },
              { name: 'address', label: 'Adresse' },
              { name: 'taxId', label: 'N° TVA' },
              { name: 'companyType', label: "Type d'entreprise" },
            ].map((f) => (
              <Field key={f.name} label={f.label}>
                <input name={f.name} value={form[f.name] || ''} onChange={onChange} className={inputClass} />
              </Field>
            ))}
            <Field label="TVA applicable">
              <select name="tvaApplicable" value={form.tvaApplicable || ''} onChange={onChange} className={selectClass}>
                <option value="">—</option>
                <option value="Yes">Oui</option>
                <option value="No">Non</option>
              </select>
            </Field>
          </div>

          <div className="flex gap-2.5 pt-4 mt-4 border-t border-[#241F1B]">
            <Button variant="primary" onClick={saveProfile} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button onClick={() => setEditing(false)} disabled={saving}>Annuler</Button>
          </div>
        </Panel>
      )}
    </Page>
  );
};

export default WorkshopPrivateProfile;
